export const MESSAGE_INPUT_SCROLLABLE_CLASS =
    "prospus-chat-body-activity-area--input-scrollable";

/** Matches `.prospus-chat-body-activity-area` min/max height. */
export const MESSAGE_COMPOSER_MIN_HEIGHT_PX = 60;
export const MESSAGE_COMPOSER_MAX_HEIGHT_PX = 250;

/**
 * Size the message composer to its content, capped at the fixed maximum.
 * A scrollbar is only needed once that maximum is reached.
 * `preferredHeight` is a user drag and can only make the box taller than the content.
 *
 * @param {{
 *   contentHeight: number,
 *   chromeHeight: number,
 *   minHeight?: number,
 *   maxHeight?: number,
 *   preferredHeight?: number | null,
 * }} options
 * @returns {{ height: number, scrollable: boolean }}
 */
/**
 * Chrome around ProseMirror from layout boxes. Ignores unused flex cross-axis
 * space when the wrap is taller than `#new-message-input`.
 *
 * @param {{
 *   wrapTop: number,
 *   wrapBottom: number,
 *   inputBottom: number,
 *   proseMirrorTop: number,
 *   proseMirrorBottom: number,
 *   wrapPaddingBottom: number,
 * }} layout
 */
export function composerChromeFromLayoutRects({
    wrapTop,
    wrapBottom,
    inputBottom,
    proseMirrorTop,
    proseMirrorBottom,
    wrapPaddingBottom,
}) {
    const belowInput = Math.max(0, wrapBottom - inputBottom);

    return (
        proseMirrorTop -
        wrapTop +
        (inputBottom - proseMirrorBottom) +
        Math.min(belowInput, Math.max(0, wrapPaddingBottom))
    );
}

function restingComposerChrome(wrap) {
    const styles = getComputedStyle(wrap);
    const padding_top = Number.parseFloat(styles.paddingTop) || 0;
    const padding_bottom = Number.parseFloat(styles.paddingBottom) || 0;
    const border = wrap.offsetHeight - wrap.clientHeight;

    return padding_top + padding_bottom + border;
}

/**
 * Space around the editor content (padding, borders, and inner offsets).
 *
 * @param {HTMLElement} wrap
 * @param {HTMLElement | null | undefined} prose_mirror
 */
export function messageComposerChromeHeight(wrap, prose_mirror) {
    if (!prose_mirror) {
        return restingComposerChrome(wrap);
    }

    const input = wrap.querySelector("#new-message-input");
    const wrap_rect = wrap.getBoundingClientRect();

    if (input) {
        const input_rect = input.getBoundingClientRect();
        const pm_rect = prose_mirror.getBoundingClientRect();
        const padding_bottom =
            Number.parseFloat(getComputedStyle(wrap).paddingBottom) || 0;

        return composerChromeFromLayoutRects({
            wrapTop: wrap_rect.top,
            wrapBottom: wrap_rect.bottom,
            inputBottom: input_rect.bottom,
            proseMirrorTop: pm_rect.top,
            proseMirrorBottom: pm_rect.bottom,
            wrapPaddingBottom: padding_bottom,
        });
    }

    const pm_rect = prose_mirror.getBoundingClientRect();

    return wrap_rect.height - pm_rect.height;
}

export function resolveMessageComposerHeight({
    contentHeight,
    chromeHeight,
    minHeight = MESSAGE_COMPOSER_MIN_HEIGHT_PX,
    maxHeight = MESSAGE_COMPOSER_MAX_HEIGHT_PX,
    preferredHeight = null,
}) {
    const fitted = Math.ceil(
        Math.max(0, contentHeight) + Math.max(0, chromeHeight),
    );
    const contentBased = Math.min(maxHeight, Math.max(minHeight, fitted));
    const preferred = Number.isFinite(preferredHeight) ? preferredHeight : null;
    const height =
        preferred === null
            ? contentBased
            : Math.min(maxHeight, Math.max(contentBased, preferred));

    return {
        height,
        scrollable: fitted > maxHeight,
    };
}
